import React, { useCallback, useEffect, useMemo, useState } from "react";
import { shuffledShots } from "@/lib/scanLoaderShots";
import { usePrefersReducedMotion } from "@/components/onboarding/shared";

// Creative phrases that mirror the real pipeline stages (scan takes ~3-7 min).
const PHRASES = [
  "Quoting your keywords for exact matches…",
  "Sweeping Reddit for real buyers…",
  "Reading forums & communities…",
  "Filtering out sellers, bots & job ads…",
  "Checking who's actually in your market…",
  "Scoring buying intent on every post…",
  "Separating hot leads from the noise…",
  "Ranking by opportunity score…",
  "Bucketing by funnel stage 🔥🌱🌊…",
  "Good leads take a minute — worth it 🍅",
  "Almost there — polishing your feed…",
];

// Real example screenshots of what a scan finds — one at a time, shuffled
// fresh per scan, advancing every 3s. Purely decorative during the wait, so a
// broken/missing image is skipped rather than shown as a broken-image icon.
function ShotSlideshow() {
  const reduced = usePrefersReducedMotion();
  const shots = useMemo(() => shuffledShots(), []);
  const [i, setI] = useState(0);

  useEffect(() => {
    if (shots.length < 2) return;
    const t = setInterval(() => setI((x) => (x + 1) % shots.length), 3000);
    return () => clearInterval(t);
  }, [shots.length]);

  if (!shots.length) return null;

  return (
    <div className="mt-8 w-full max-w-sm">
      <img
        key={i}
        src={`/scan-loader-shots/${shots[i]}`}
        alt="Example of a real signal Leadmato finds"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        className={`w-full rounded-xl border border-lm-border shadow-sm ${reduced ? "" : "animate-[fadeIn_0.4s_ease]"}`}
      />
    </div>
  );
}

export default function ScanLoader({ startedAt }) {
  // startedAt (ms) = the scan's real start. When provided, the counter is
  // computed from it, so it survives a refresh instead of restarting at 0.
  // useCallback, not a plain arrow: the timer effect below depends on this, and
  // a fresh identity every render would tear down and rebuild both intervals on
  // each tick — which resets the phrase interval every second, so the phrases
  // would never advance past the first one. Now its identity tracks `startedAt`,
  // which is the only thing that should restart the timers.
  const elapsed = useCallback(
    () => (startedAt ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000)) : 0),
    [startedAt]
  );
  const [i, setI] = useState(0);
  const [sec, setSec] = useState(elapsed);

  useEffect(() => {
    const phrase = setInterval(() => setI((x) => (x + 1) % PHRASES.length), 2600);
    const timer = setInterval(
      () => setSec(startedAt ? elapsed() : (s) => s + 1),
      1000
    );
    return () => { clearInterval(phrase); clearInterval(timer); };
  }, [startedAt, elapsed]);

  return (
    <div
      data-testid="scan-loader"
      className="rounded-2xl border border-lm-border bg-lm-surface py-16 px-6 flex flex-col items-center text-center"
    >
      <div className="lm-scan-loader mb-12" />
      <div className="h-6 flex items-center justify-center">
        <p key={i} className="text-lm-text font-medium animate-[fadeIn_0.4s_ease]">
          {PHRASES[i]}
        </p>
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs font-mono text-lm-text3">
        <span className="tabular-nums">{sec}s</span>
        <span className="w-1 h-1 rounded-full bg-lm-text3/50" />
        <span>usually 3–7 min · one scan, up to 5 platforms</span>
      </div>
      <ShotSlideshow />
    </div>
  );
}
